import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WebhookConfig from "../components/WebhookConfig";
import { IntegrationsMapping } from "../IntegrationsMapping";
import xsiam_integrations from "./images/xsiam_integrations.png";
import xsiam_webhook_1 from "./images/xsiam_webhook_1.png";
import xsiam_webhook_2 from "./images/xsiam_webhook_2.png";
import PaloAltoCorteXSIAMConnectForm from "./PaloAltoCortexXSIAMonnectForm";

const INTEGRATION_CODE_NAME = "paloalto_cortex_xsiam";

const PaloAltoCortexXSIAMIntegration = () => {
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerPaloAltoCortexXSIAMIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerPaloAltoCortexXSIAMIntegration(
      values.username,
      values.password,
      values.webhook_url
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integration successful`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? ""
            : `Once integrated, the Breeze platform will send alerts about new detections to the ${integration.displayName} webhook.`
        }
        image={logos.PaloAltoCortexLogo}
        name={integration.displayName}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <WebhookConfig
              toolName={integration.codeName}
              toolDisplayName={integration.displayName}
            />
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Palo Alto Cortex XSIAM">
                  <Typography variant={"body2"}>
                    Sign in to your Palo Alto Cortex XSIAM console as an
                    administrator.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Install the Generic Webhooks integration - Optional">
                  <Typography variant={"body2"}>
                    If you have not installed the Generic Webhooks integration
                    yet, follow these steps:
                  </Typography>
                  <Typography variant={"body2"}>
                    Navigate to <b>Marketplace </b>.
                  </Typography>
                  <Typography variant={"body2"}>
                    Search for <b>Generic Webhooks</b>.
                  </Typography>
                  <Typography variant={"body2"}>
                    Install the <b>Generic Webhooks </b>
                    integration.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create a new webhook instance">
                  <Typography variant={"body2"}>
                    Go to{" "}
                    <b>
                      Settings {">"} Configurations {">"} Automation & Feed
                      Integrations
                    </b>
                    .
                  </Typography>
                  <Typography variant={"body2"}>
                    Search for <b>Generic Webhook</b> in the instances list and
                    click <b>Add instance</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: xsiam_integrations,
                        altext:
                          "Go to Settings > Configurations > Automation & Feed Integrations and search for Generic Webhook",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Enter a unique name for the instance e.g.
                    <b> Breeze_Platform</b>.
                  </Typography>
                  <Typography variant={"body2"}>
                    Select a username and password (you can use any values you
                    want) and paste them in the form below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: xsiam_webhook_1,
                        altext:
                          "Enter a unique name and configure username and password",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy Your XSIAM Webhook URL">
                  <Typography variant={"body2"}>
                    Your XSIAM Webhook URL is the URL of the webhook instance
                    you created.
                  </Typography>
                  <Typography variant={"body2"}>
                    It should look like: https://
                    <b>
                      {"<"}XSIAM-Domain{">"}
                    </b>
                    /xsoar/instance/execute/
                    <b>
                      {"<"}Instance-Name{">"}
                    </b>
                  </Typography>
                  <Typography variant={"body2"}>
                    Copy the webhook URL and paste it in the form below.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: xsiam_webhook_2,
                        altext: "Copy the webhook URL",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the webhook credentials here">
                  <Box width={700}>
                    <PaloAltoCorteXSIAMConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default PaloAltoCortexXSIAMIntegration;
